import { useState } from 'react';
import {
  ArrowUpOutlined, RightOutlined, ThunderboltOutlined,
  EyeOutlined, CheckCircleOutlined,
} from '@ant-design/icons';

/**
 * 机会列表组件
 * 展示高潜商业机会，支持展开详情和操作
 */
export default function OpportunityList({ opportunities = [] }) {
  const [expandedId, setExpandedId] = useState(null);
  const [actedIds, setActedIds] = useState(new Set());

  const handleAct = (id) => {
    setActedIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  if (!opportunities.length) {
    return (
      <div style={{
        padding: '48px 24px',
        textAlign: 'center',
        borderRadius: 16,
        background: 'rgba(255,255,255,0.015)',
        border: '1px solid rgba(255,255,255,0.04)',
      }}>
        <p style={{ fontSize: 14, color: '#64766D' }}>暂无高潜机会，等待雷达扫描...</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {opportunities.map((op, i) => (
        <OpportunityCard
          key={op.id || i}
          opportunity={op}
          isExpanded={expandedId === op.id}
          isActed={actedIds.has(op.id)}
          onToggle={() => setExpandedId(expandedId === op.id ? null : op.id)}
          onAct={() => handleAct(op.id)}
        />
      ))}
    </div>
  );
}

function OpportunityCard({ opportunity, isExpanded, isActed, onToggle, onAct }) {
  const score = opportunity.score || 0;
  const scoreColor = score >= 85 ? '#E8C85A' : score >= 75 ? '#69B89A' : '#8EB9D6';

  return (
    <div style={{
      borderRadius: 16,
      background: isExpanded ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.015)',
      border: `1px solid ${isExpanded ? 'rgba(232,200,90,0.12)' : 'rgba(255,255,255,0.04)'}`,
      overflow: 'hidden',
      transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    }}>
      {/* ── 主行 ── */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 20,
          padding: '20px 24px',
          cursor: 'pointer',
          transition: 'background 200ms ease',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        {/* 机会指数 */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          minWidth: 56,
        }}>
          <span style={{
            fontSize: 24, fontWeight: 600, color: scoreColor,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.02em',
            lineHeight: 1,
          }}>
            {score}
          </span>
          <span style={{
            fontSize: 9, color: '#64766D', letterSpacing: '0.06em',
            textTransform: 'uppercase', marginTop: 2,
          }}>
            INDEX
          </span>
        </div>

        {/* 分数进度条 */}
        <div style={{
          width: 4, height: 40, borderRadius: 2,
          background: 'rgba(255,255,255,0.05)',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        }}>
          <div style={{
            width: '100%', height: `${score}%`,
            borderRadius: 2,
            background: `linear-gradient(to top, ${scoreColor}90, ${scoreColor})`,
            transition: 'height 600ms ease',
          }} />
        </div>

        {/* 内容 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 15, fontWeight: 600, color: '#F3F5EF',
            marginBottom: 4,
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {opportunity.title}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            fontSize: 12, color: '#64766D',
          }}>
            <span>{opportunity.scenarioName}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <ArrowUpOutlined style={{ fontSize: 10, color: '#69B89A' }} />
              {opportunity.expectedGrowth}%
            </span>
            <span>置信度 {opportunity.confidence}%</span>
          </div>
        </div>

        {/* 状态 / 操作 */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          flexShrink: 0,
        }}>
          {isActed ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 12, color: '#69B89A', fontWeight: 500,
            }}>
              <CheckCircleOutlined /> 已采纳
            </span>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); onAct(); }}
              style={{
                height: 34, padding: '0 16px',
                borderRadius: 17,
                border: '1px solid rgba(232,200,90,0.2)',
                background: 'rgba(232,200,90,0.05)',
                color: '#E8C85A',
                fontSize: 12, fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 200ms ease',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => {
                e.target.style.background = 'rgba(232,200,90,0.1)';
              }}
              onMouseLeave={e => {
                e.target.style.background = 'rgba(232,200,90,0.05)';
              }}
            >
              <ThunderboltOutlined style={{ marginRight: 4 }} />
              采纳
            </button>
          )}
          <RightOutlined style={{
            fontSize: 12, color: '#4A6070',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)',
            transition: 'transform 200ms ease',
          }} />
        </div>
      </div>

      {/* ── 展开详情 ── */}
      {isExpanded && (
        <div style={{
          padding: '0 24px 24px',
          paddingLeft: 100,
          animation: 'fadeSlideDown 200ms ease',
        }}>
          {/* 推荐理由 */}
          <div style={{
            padding: '14px 16px',
            borderRadius: 12,
            background: 'rgba(232,200,90,0.03)',
            border: '1px solid rgba(232,200,90,0.08)',
            marginBottom: 14,
          }}>
            <div style={{
              fontSize: 11, color: '#E8C85A', fontWeight: 500,
              marginBottom: 6, letterSpacing: '0.04em',
              textTransform: 'upperCase',
            }}>
              AI 推理
            </div>
            <div style={{
              fontSize: 13, color: '#A9B8B0',
              lineHeight: 1.6,
            }}>
              {opportunity.reason || '基于多信号融合分析，该品类在当前场景下需求增长确定性高，建议及时上架。'}
            </div>
          </div>

          {/* 推荐商品 */}
          {opportunity.recommendedProducts && opportunity.recommendedProducts.length > 0 && (
            <div>
              <div style={{
                fontSize: 12, color: '#64766D',
                marginBottom: 8, fontWeight: 500,
              }}>
                推荐商品
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {opportunity.recommendedProducts.map((product, j) => (
                  <span key={j} style={{
                    padding: '4px 12px', borderRadius: 10,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    fontSize: 12, color: '#A9B8B0',
                    cursor: 'pointer',
                    transition: 'all 150ms ease',
                  }}
                    onMouseEnter={e => {
                      e.target.style.background = 'rgba(232,200,90,0.06)';
                      e.target.style.borderColor = 'rgba(232,200,90,0.15)';
                    }}
                    onMouseLeave={e => {
                      e.target.style.background = 'rgba(255,255,255,0.03)';
                      e.target.style.borderColor = 'rgba(255,255,255,0.06)';
                    }}
                  >
                    {product}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 操作按钮组 */}
          <div style={{
            display: 'flex', gap: 8,
            marginTop: 16, paddingTop: 14,
            borderTop: '1px solid rgba(255,255,255,0.04)',
          }}>
            {[
              { icon: <ThunderboltOutlined />, label: '一键上架', primary: true },
              { icon: <EyeOutlined />, label: '加入观察' },
            ].map((btn, j) => (
              <button key={j} style={{
                height: 32, padding: '0 16px',
                borderRadius: 16,
                border: btn.primary
                  ? '1px solid rgba(232,200,90,0.25)'
                  : '1px solid rgba(255,255,255,0.08)',
                background: btn.primary
                  ? 'rgba(232,200,90,0.08)'
                  : 'transparent',
                color: btn.primary ? '#E8C85A' : '#8BA8B8',
                fontSize: 12, fontWeight: 500,
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 5,
                transition: 'all 150ms ease',
              }}>
                {btn.icon}
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
